import { useState } from 'react';
import { Modal, Form, Input } from 'antd';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

interface ProjectCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, slug?: string) => Promise<void>;
}

const ProjectCreateModal = ({ open, onClose, onSubmit }: ProjectCreateModalProps) => {
  const { _ } = useLingui();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .trim();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    if (!isSlugManuallyEdited) {
      const slug = generateSlug(newName);
      form.setFieldsValue({ slug });
    }
  };

  const handleSlugChange = () => {
    setIsSlugManuallyEdited(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(values.name, values.slug);
      form.resetFields();
      setIsSlugManuallyEdited(false);
      onClose();
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsSlugManuallyEdited(false);
    onClose();
  };

  return (
    <Modal
      title={<Trans>Create Project</Trans>}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={<Trans>Create</Trans>}
      cancelText={<Trans>Cancel</Trans>}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        name="project_create"
        autoComplete="off"
      >
        <Form.Item
          label={<Trans>Project Name</Trans>}
          name="name"
          rules={[
            { required: true, message: _(msg`Please enter project name`) },
            { whitespace: true, message: _(msg`Project name cannot be empty`) },
          ]}
        >
          <Input
            placeholder={_(msg`Enter project name`)}
            onChange={handleNameChange}
          />
        </Form.Item>

        <Form.Item
          label={<Trans>Slug</Trans>}
          name="slug"
          help={_(msg`URL-friendly identifier, automatically generated from name`)}
          rules={[
            { required: true, message: _(msg`Slug is required`) },
            { max: 255, message: _(msg`Slug must be less than 255 characters`) },
            {
              pattern: /^[a-z0-9-]+$/,
              message: _(msg`Slug can only contain lowercase letters, numbers, and hyphens`),
            },
          ]}
        >
          <Input
            placeholder={_(msg`project-slug`)}
            onChange={handleSlugChange}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProjectCreateModal;
